import UE from '@msgai/passport-authz-js';
import { normalizeAccessToken } from './access-token.js';
import {
  DEFAULT_DEV_ACCESS_TOKEN,
  DEFAULT_DEV_BOT,
  DEFAULT_DEV_PASSPORT_USER,
} from './defaults.js';

const DEFAULT_USER_STORAGE_KEY = 'user';
const DEFAULT_PASSPORT_STORAGE_KEY = 'passport-user';
const DEFAULT_BOT_STORAGE_KEY = 'currentBot';
const DEFAULT_BOT_ID_STORAGE_KEY = 'currentBotId';
const DEFAULT_ACCESS_TOKEN_STORAGE_KEY = 'access-token';
const DEFAULT_RECIPE_TOKEN = 'PWUWERREWT';
const DEFAULT_SEED_VERSION_STORAGE_KEY = 'dev-auth-seed-version';

// Bump whenever the bundled dev fixture changes — especially
// DEFAULT_DEV_PASSPORT_USER's policy. `bootstrapDevAuth` reuses the cached
// identity in localStorage, so without a version gate a fixture/policy edit
// never reaches existing dev sessions. A mismatch forces a one-time re-seed of
// the default identity on the next load.
const DEV_SEED_VERSION = '2026-06-23';

export type DevPassportUser = Record<string, unknown>;

export interface DevBotContext extends Record<string, unknown> {
  id: string;
  orgId?: number;
}

export interface DevAuthBootstrapOptions {
  enabled?: boolean;
  mockUser?: DevPassportUser;
  mockBot?: DevBotContext;
  storage?: Storage;
  userStorageKey?: string;
  passportStorageKey?: string | null;
  botStorageKey?: string;
  botIdStorageKey?: string;
  accessToken?: string;
  accessTokenStorageKey?: string;
  recipeToken?: string;
  includeRecipeToken?: boolean;
  setPassportEntity?: boolean;
  seedVersionStorageKey?: string;
}

export interface DevAuthBootstrapResult {
  enabled: boolean;
  reason?: string;
  storage?: Storage;
  user?: DevPassportUser;
  bot?: DevBotContext;
  accessToken?: string;
  headers?: Record<string, string>;
}

export interface DevHeaderOptions {
  botId?: string;
  botRefId?: string;
  recipeToken?: string;
  includeRecipeToken?: boolean;
  storage?: Storage;
  botIdStorageKey?: string;
  accessTokenStorageKey?: string;
  accessToken?: string;
}

export function bootstrapDevAuth(options: DevAuthBootstrapOptions = {}): DevAuthBootstrapResult {
  const shouldEnable = options.enabled ?? isDevEnvironment();
  if (!shouldEnable) {
    return { enabled: false, reason: 'env:disabled' };
  }

  const storage = options.storage ?? getStorage();
  if (!storage) {
    return { enabled: false, reason: 'storage:unavailable' };
  }

  const userKey = options.userStorageKey ?? DEFAULT_USER_STORAGE_KEY;
  const passportKey = options.passportStorageKey ?? DEFAULT_PASSPORT_STORAGE_KEY;
  const botKey = options.botStorageKey ?? DEFAULT_BOT_STORAGE_KEY;
  const botIdKey = options.botIdStorageKey ?? DEFAULT_BOT_ID_STORAGE_KEY;
  const tokenKey = options.accessTokenStorageKey ?? DEFAULT_ACCESS_TOKEN_STORAGE_KEY;

  // Re-seed the cached identity when the bundled fixture version changed, so
  // policy edits in defaults.ts take effect without a manual localStorage clear.
  const seedVersionKey = options.seedVersionStorageKey ?? DEFAULT_SEED_VERSION_STORAGE_KEY;
  const seedStale = storage.getItem(seedVersionKey) !== DEV_SEED_VERSION;

  const user = ensureObject(
    storage,
    userKey,
    options.mockUser ?? DEFAULT_DEV_PASSPORT_USER,
    seedStale
  );
  if (passportKey) {
    writeJson(storage, passportKey, user);
  }
  if (seedStale) {
    storage.setItem(seedVersionKey, DEV_SEED_VERSION);
  }
  const bot = ensureObject(storage, botKey, options.mockBot ?? DEFAULT_DEV_BOT);
  if (!storage.getItem(botIdKey)) {
    storage.setItem(botIdKey, bot.id);
  }

  const accessToken = ensureAccessToken(storage, tokenKey, options.accessToken);
  const headers = buildDevHeaders({
    storage,
    botIdStorageKey: botIdKey,
    accessTokenStorageKey: tokenKey,
    recipeToken: options.recipeToken,
    includeRecipeToken: options.includeRecipeToken,
  });

  if (options.setPassportEntity !== false) {
    configurePassport(user, bot);
  }

  return { enabled: true, storage, user, bot, accessToken, headers };
}

export function buildDevHeaders(options: DevHeaderOptions = {}): Record<string, string> {
  const storage = options.storage ?? getStorage();
  const botIdKey = options.botIdStorageKey ?? DEFAULT_BOT_ID_STORAGE_KEY;
  const tokenKey = options.accessTokenStorageKey ?? DEFAULT_ACCESS_TOKEN_STORAGE_KEY;
  const botId = options.botId ?? readStoredString(storage, botIdKey) ?? DEFAULT_DEV_BOT.id;
  const botRefId = options.botRefId ?? botId;
  const cookie = normalizeAccessToken(
    options.accessToken ?? storage?.getItem(tokenKey) ?? DEFAULT_DEV_ACCESS_TOKEN
  );
  const headers: Record<string, string> = {};

  if (options.includeRecipeToken !== false) {
    headers['X-RECIPE-TOKEN'] = options.recipeToken ?? DEFAULT_RECIPE_TOKEN;
  }

  if (botId) {
    headers['X-BOT-ID'] = botId;
  }
  if (botRefId) {
    headers['X-BOT-REF-ID'] = botRefId;
  }
  if (cookie) {
    headers['x-cookie'] = cookie;
  }

  return headers;
}

/**
 * aistudio's `vueLocalStorage.set` JSON-stringifies every value, so keys shared with the host
 * (`currentBotId`, `access-token`) come back quoted — `"bot-1"` — and a raw read ships the quotes
 * in the header, which the backend rejects.
 */
function readStoredString(storage: Storage | undefined, key: string): string | undefined {
  const raw = storage?.getItem(key);
  if (!raw) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'string' ? parsed || undefined : raw;
  } catch {
    return raw;
  }
}

function ensureObject<T extends Record<string, unknown>>(
  storage: Storage,
  key: string,
  fallback: T,
  forceWrite = false
): T {
  if (!forceWrite) {
    const existing = readJson<T>(storage, key);
    if (existing) {
      return existing;
    }
  }
  writeJson(storage, key, fallback);
  return fallback;
}

function ensureAccessToken(storage: Storage, key: string, provided?: string): string {
  const current = storage.getItem(key);
  const token = provided ?? current ?? DEFAULT_DEV_ACCESS_TOKEN;
  storage.setItem(key, token);
  return token;
}

function writeJson(storage: Storage, key: string, value: unknown) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`dev-auth: failed to persist ${key}`, error);
  }
}

function readJson<T>(storage: Storage, key: string): T | undefined {
  try {
    const value = storage.getItem(key);
    if (!value) {
      return undefined;
    }
    return JSON.parse(value) as T;
  } catch (error) {
    console.warn(`dev-auth: failed to parse ${key}`, error);
    return undefined;
  }
}

function configurePassport(user: DevPassportUser, bot: DevBotContext) {
  try {
    // biome-ignore lint/suspicious/noExplicitAny: Module interop workaround for nodenext resolution
    const PassportUE = UE as any;
    if (typeof PassportUE?.setEntity === 'function') {
      const userOrgId = typeof user.orgId === 'number' ? user.orgId : undefined;
      const userOrg = user.org;
      const nestedOrgId =
        typeof userOrg === 'object' && userOrg && 'id' in userOrg && typeof userOrg.id === 'number'
          ? userOrg.id
          : undefined;

      PassportUE.setEntity({
        user,
        currentOrgId: bot.orgId ?? userOrgId ?? nestedOrgId ?? -1,
        currentBotId: bot.id ?? '',
      });
    }
  } catch (error) {
    console.warn('dev-auth: unable to configure passport entity', error);
  }
}

function isDevEnvironment(): boolean {
  // eslint-disable-next-line no-undef
  return typeof process === 'undefined' || process.env?.NODE_ENV !== 'production';
}

function getStorage(): Storage | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}
